/**
 * AutoAnimateList — drop-in wrapper that animates additions, removals,
 * and reorderings of its children.
 *
 * Built on `@formkit/auto-animate`, which observes the wrapper's DOM
 * subtree and runs FLIP-style transitions whenever the child set
 * changes. The consumer keeps writing React the normal way — no
 * keyframes, no `AnimatePresence`, no per-item config — and the list
 * animates automatically.
 *
 * Honors `prefers-reduced-motion`: `auto-animate` queries the same
 * media feature internally, so the wrapper degrades to a static
 * rerender without any extra logic here.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useRef } from "react";
import autoAnimate, { type AutoAnimateOptions } from "@formkit/auto-animate";

export interface AutoAnimateListProps {
  /** List items. */
  readonly children: React.ReactNode;
  /** Animation duration (ms). Defaults to 220. */
  readonly durationMs?: number;
  /** CSS easing. Defaults to `ease-in-out`. */
  readonly easing?: AutoAnimateOptions["easing"];
  /**
   * HTML tag used as the wrapper. Defaults to `div`. Use `ul` / `ol`
   * when the list is semantically ordered or unordered.
   */
  readonly as?: "div" | "ul" | "ol" | "section";
  /** Optional className applied to the wrapper. */
  readonly className?: string;
}

/** Wraps its children with `@formkit/auto-animate` for list transitions. */
export const AutoAnimateList = React.memo(function AutoAnimateList({
  children,
  durationMs = 220,
  easing = "ease-in-out",
  as: Tag = "div",
  className,
}: AutoAnimateListProps): React.JSX.Element {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current === null) return;
    autoAnimate(ref.current, { duration: durationMs, easing });
  }, [durationMs, easing]);

  return React.createElement(
    Tag,
    {
      ref,
      className,
      "data-csk-auto-animate": "",
    },
    children,
  );
});
